import React, { useCallback, useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import styles from './Dropdown.module.css';

// items: [{label, icon, onClick, href, danger, disabled} | {separator: true}]
// Pass `content` instead of `items` for a fully custom panel body (e.g. the
// notification list) — keyboard nav over discrete items is skipped in that case.
function Dropdown({ trigger, items, content, align = 'right', panelClassName = '', onOpenChange }) {
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const wrapperRef = useRef(null);

  const setOpenState = useCallback(
    (next) => {
      setOpen(next);
      onOpenChange?.(next);
    },
    [onOpenChange]
  );

  useEffect(() => {
    if (!open) return;
    function handleClickOutside(e) {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpenState(false);
    }
    function handleKeyDown(e) {
      if (e.key === 'Escape') setOpenState(false);
      if (!items) return;
      const selectable = items.filter((it) => !it.separator);
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setActiveIndex((i) => (i + 1) % selectable.length);
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setActiveIndex((i) => (i - 1 + selectable.length) % selectable.length);
      }
      if (e.key === 'Enter' && activeIndex >= 0) {
        selectable[activeIndex]?.onClick?.();
        setOpenState(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open, items, activeIndex, setOpenState]);

  useEffect(() => {
    if (!open) setActiveIndex(-1);
  }, [open]);

  let selectableIdx = -1;

  return (
    <div className={styles.wrapper} ref={wrapperRef}>
      <button
        type="button"
        className={styles.trigger}
        onClick={() => setOpenState(!open)}
        aria-haspopup="menu"
        aria-expanded={open}
      >
        {trigger}
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            className={[styles.panel, align === 'left' ? styles.left : '', panelClassName].filter(Boolean).join(' ')}
            role={content ? undefined : 'menu'}
            initial={{ opacity: 0, scale: 0.96, y: -4 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.96, y: -4 }}
            transition={{ duration: 0.14 }}
          >
            {content
              ? content
              : items.map((it, i) => {
                  if (it.separator) return <div key={i} className={styles.separator} />;
                  selectableIdx += 1;
                  const isActive = selectableIdx === activeIndex;
                  const Component = it.href ? 'a' : 'button';
                  return (
                    <Component
                      key={it.label}
                      href={it.href}
                      role="menuitem"
                      disabled={it.disabled}
                      className={[styles.item, it.danger ? styles.danger : '', isActive ? styles.active : '']
                        .filter(Boolean)
                        .join(' ')}
                      onClick={() => {
                        it.onClick?.();
                        setOpenState(false);
                      }}
                    >
                      {it.icon && <it.icon size={16} />}
                      {it.label}
                    </Component>
                  );
                })}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default Dropdown;
